import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../store/authStore'
import {
  computeCit,
  endOfMonthIso,
  startOfYearIso,
  type CitComputationResult,
  type BusinessTaxRatesInput,
} from '../lib/citComputation'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc = (fn: string, args: Record<string, unknown>): any =>
  (supabase as any).rpc(fn, args)

const toNum = (v: string | number | null | undefined): number =>
  v === null || v === undefined ? 0 : typeof v === 'number' ? v : parseFloat(v)

// ─── Historical accruals ────────────────────────────────────────────────────

export interface CitAccrualHistoryRow {
  id: string
  periodEndDate: string
  ytdProfitBeforeTax: number
  ytdTurnover: number
  companySize: 'small' | 'medium' | 'large'
  citRatePercent: number
  edtRatePercent: number
  minimumTaxRatePercent: number
  citAmount: number
  edtAmount: number
  minimumTaxAmount: number
  totalTaxCharge: number
  priorYtdAccrued: number
  periodCharge: number
  journalEntryId: string | null
  journalEntryNumber: string | null
  postedAt: string
  notes: string | null
}

type AccrualRow = {
  id: string
  period_end_date: string
  ytd_profit_before_tax: string | number
  ytd_turnover: string | number
  company_size: 'small' | 'medium' | 'large'
  cit_rate_percent: string | number
  edt_rate_percent: string | number
  minimum_tax_rate_percent: string | number
  cit_amount: string | number
  edt_amount: string | number
  minimum_tax_amount: string | number
  total_tax_charge: string | number
  prior_ytd_accrued: string | number
  period_charge: string | number
  journal_entry_id: string | null
  posted_at: string
  notes: string | null
  journal_entries: { entry_number: string } | null
}

function rowToHistory(r: AccrualRow): CitAccrualHistoryRow {
  return {
    id: r.id,
    periodEndDate: r.period_end_date,
    ytdProfitBeforeTax: toNum(r.ytd_profit_before_tax),
    ytdTurnover: toNum(r.ytd_turnover),
    companySize: r.company_size,
    citRatePercent: toNum(r.cit_rate_percent),
    edtRatePercent: toNum(r.edt_rate_percent),
    minimumTaxRatePercent: toNum(r.minimum_tax_rate_percent),
    citAmount: toNum(r.cit_amount),
    edtAmount: toNum(r.edt_amount),
    minimumTaxAmount: toNum(r.minimum_tax_amount),
    totalTaxCharge: toNum(r.total_tax_charge),
    priorYtdAccrued: toNum(r.prior_ytd_accrued),
    periodCharge: toNum(r.period_charge),
    journalEntryId: r.journal_entry_id,
    journalEntryNumber: r.journal_entries?.entry_number ?? null,
    postedAt: r.posted_at,
    notes: r.notes,
  }
}

export function useCitAccrualHistory() {
  const tenantId = useAuthStore(s => s.tenant?.id ?? '')
  return useQuery<CitAccrualHistoryRow[]>({
    queryKey: ['cit-accruals', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await db('cit_accruals')
        .select(
          `id, period_end_date,
           ytd_profit_before_tax, ytd_turnover, company_size,
           cit_rate_percent, edt_rate_percent, minimum_tax_rate_percent,
           cit_amount, edt_amount, minimum_tax_amount,
           total_tax_charge, prior_ytd_accrued, period_charge,
           journal_entry_id, posted_at, notes,
           journal_entries ( entry_number )`,
        )
        .eq('tenant_id', tenantId)
        .order('period_end_date', { ascending: false })

      if (error) throw error
      return ((data ?? []) as AccrualRow[]).map(rowToHistory)
    },
  })
}

// ─── Existing accrual lookup for a specific period ──────────────────────────

export function useCitAccrualForPeriod(periodEndIso: string) {
  const tenantId = useAuthStore(s => s.tenant?.id ?? '')
  return useQuery<CitAccrualHistoryRow | null>({
    queryKey: ['cit-accrual-period', tenantId, periodEndIso],
    enabled: !!tenantId && !!periodEndIso,
    queryFn: async () => {
      const { data, error } = await db('cit_accruals')
        .select(
          `id, period_end_date,
           ytd_profit_before_tax, ytd_turnover, company_size,
           cit_rate_percent, edt_rate_percent, minimum_tax_rate_percent,
           cit_amount, edt_amount, minimum_tax_amount,
           total_tax_charge, prior_ytd_accrued, period_charge,
           journal_entry_id, posted_at, notes,
           journal_entries ( entry_number )`,
        )
        .eq('tenant_id', tenantId)
        .eq('period_end_date', periodEndIso)
        .maybeSingle()

      if (error) throw error
      if (!data) return null
      return rowToHistory(data as AccrualRow)
    },
  })
}

// ─── Client-side preview (matches the RPC math exactly) ─────────────────────

export interface CitPreview extends CitComputationResult {
  ytdProfitBeforeTax: number
  ytdTurnover: number
  periodEndDate: string
  periodStartDate: string
}

type IncomeStatementRow = {
  section: string
  amount: number | string | null
  is_subtotal: boolean
}

type BusinessTaxSettingsRow = BusinessTaxRatesInput & {
  id: string
  tenant_id: string
}

export interface CitAdjustmentTotals {
  totalDisallowables: number
  totalAllowables: number
}

export function useCitAccrualPreview(
  year: number,
  month: number,
  adjustmentTotals: CitAdjustmentTotals = { totalDisallowables: 0, totalAllowables: 0 },
): {
  data: CitPreview | null
  isLoading: boolean
  error: Error | null
} {
  const tenantId = useAuthStore(s => s.tenant?.id ?? '')

  const periodEndDate = endOfMonthIso(year, month)
  const periodStartDate = startOfYearIso(year)

  const settingsQ = useQuery<BusinessTaxSettingsRow | null>({
    queryKey: ['business-tax-settings', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await db('business_tax_settings')
        .select(`id, tenant_id,
                 cit_rate_small, cit_rate_medium, cit_rate_large,
                 cit_turnover_small_threshold, cit_turnover_medium_threshold,
                 tet_rate_small, tet_rate_large,
                 minimum_tax_rate`)
        .eq('tenant_id', tenantId)
        .maybeSingle()
      if (error) throw error
      return (data ?? null) as BusinessTaxSettingsRow | null
    },
  })

  const isStmtQ = useQuery<{ ytdPbt: number; ytdTurnover: number }>({
    queryKey: ['cit-preview-is', tenantId, periodEndDate],
    enabled: !!tenantId && !!periodEndDate,
    queryFn: async () => {
      const { data, error } = await rpc('get_income_statement', {
        p_tenant_id: tenantId,
        p_location_id: undefined,
        p_from_date: periodStartDate,
        p_to_date: periodEndDate,
        p_include_zero: false,
      })
      if (error) throw new Error(error.message)
      const rows = (data ?? []) as IncomeStatementRow[]

      let ytdPbt = 0
      let ytdTurnover = 0
      for (const r of rows) {
        const amt = Number(r.amount ?? 0)
        if (r.is_subtotal && r.section === 'profit_before_tax') {
          ytdPbt = amt
        } else if (!r.is_subtotal && r.section === 'revenue') {
          ytdTurnover += amt
        }
      }
      return { ytdPbt, ytdTurnover }
    },
  })

  const priorQ = useQuery<number>({
    queryKey: ['cit-prior-ytd', tenantId, year, periodEndDate],
    enabled: !!tenantId && !!periodEndDate,
    queryFn: async () => {
      const yearStart = startOfYearIso(year)
      const { data, error } = await db('cit_accruals')
        .select('total_tax_charge, period_end_date')
        .eq('tenant_id', tenantId)
        .gte('period_end_date', yearStart)
        .lt('period_end_date', periodEndDate)

      if (error) throw error
      const rows = (data ?? []) as { total_tax_charge: string | number }[]
      return rows.reduce((s, r) => s + toNum(r.total_tax_charge), 0)
    },
  })

  const isLoading = settingsQ.isLoading || isStmtQ.isLoading || priorQ.isLoading
  const error = (settingsQ.error ?? isStmtQ.error ?? priorQ.error) as Error | null

  if (isLoading || !settingsQ.data || !isStmtQ.data) {
    return { data: null, isLoading, error }
  }

  const result = computeCit({
    ytdProfitBeforeTax: isStmtQ.data.ytdPbt,
    ytdTurnover: isStmtQ.data.ytdTurnover,
    totalDisallowables: adjustmentTotals.totalDisallowables,
    totalAllowables: adjustmentTotals.totalAllowables,
    priorYtdAccrued: priorQ.data ?? 0,
    settings: settingsQ.data,
  })

  return {
    data: {
      ...result,
      ytdProfitBeforeTax: isStmtQ.data.ytdPbt,
      ytdTurnover: isStmtQ.data.ytdTurnover,
      periodEndDate,
      periodStartDate,
    },
    isLoading: false,
    error: null,
  }
}

// ─── Post CIT accrual ───────────────────────────────────────────────────────

export interface PostCitAccrualInput {
  periodEndIso: string
}

export function usePostCitAccrual() {
  const qc = useQueryClient()
  const tenantId = useAuthStore(s => s.tenant?.id ?? '')
  const userId = useAuthStore(s => s.user?.id ?? null)

  return useMutation<string | null, Error, PostCitAccrualInput>({
    mutationFn: async (input) => {
      if (!tenantId) throw new Error('Tenant not loaded')
      const { data, error } = await rpc('post_cit_accrual_atomic', {
        p_tenant_id: tenantId,
        p_period_end: input.periodEndIso,
        p_created_by: userId,
      })
      if (error) throw error
      return (data ?? null) as string | null
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cit-accruals', tenantId] })
      qc.invalidateQueries({ queryKey: ['cit-accrual-period', tenantId] })
      qc.invalidateQueries({ queryKey: ['cit-prior-ytd', tenantId] })
    },
  })
}

export function translateCitAccrualError(message: string): string {
  const m = message.toLowerCase()
  if (m.includes('cit already accrued')) {
    return 'CIT has already been accrued for this period. Pick a different month.'
  }
  if (m.includes('must be a month-end date')) {
    return 'Internal error: period must be the last day of a month.'
  }
  if (m.includes('no business_tax_settings')) {
    return 'Business tax settings are missing. Configure them under Business Settings → Finance → Tax.'
  }
  if (m.includes('account 7100')) {
    return 'Tax Expense account (7100) is missing from your chart of accounts. Contact support.'
  }
  if (m.includes('account 2125')) {
    return 'Income Tax Payable account (2125) is missing. Re-run migration 149 or contact support.'
  }
  if (m.includes('does not balance')) {
    return 'Could not post the journal entry (it did not balance). Contact support.'
  }
  return message
}
